import { useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, runAiJob } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { MagnifyingGlass, Sparkle, Plus, Check } from "@phosphor-icons/react";
import { Highlight } from "./highlight";
import { FocusInstructions } from "./FocusInstructions";

const FnChips = ({ all, value, onChange }) => (
  <div className="flex flex-wrap gap-1" data-testid="lens-term-functions">
    {all.map((f) => {
      const on = value.includes(f);
      return (
        <button key={f} type="button" data-testid={`lens-term-fn-${f}`}
          onClick={() => onChange(on ? value.filter((x) => x !== f) : [...value, f])}
          className={`text-[10px] font-mono px-1.5 py-0.5 border transition-colors ${on ? "bg-[#071B3E] text-white border-[#071B3E]" : "border-border text-muted-foreground hover:border-foreground"}`}>
          {f}
        </button>
      );
    })}
  </div>
);

const MatchRow = ({ m, term, added, onAdd, reason }) => (
  <div className="flex items-start gap-2 px-2 py-1.5 border-t border-border text-xs" data-testid={`lens-term-match-${m.standard_id}-${m.req_number}`}>
    <div className="flex-1">
      <span className="font-mono font-semibold">{m.standard_id} {m.req_number}</span>
      {m.matched_parts?.length > 0 && <span className="text-[10px] text-muted-foreground"> · Parts {m.matched_parts.join(", ")}</span>}
      {(m.functions || []).length > 0 && <span className="text-[10px] text-muted-foreground"> · {m.functions.join(", ")}</span>}
      <div className="text-muted-foreground">{reason || <Highlight text={m.snippet} term={term} />}</div>
    </div>
    <Button size="sm" variant="ghost" disabled={added} onClick={() => onAdd([m])} className="rounded-none h-6 px-2"
      data-testid={`lens-term-add-${m.standard_id}-${m.req_number}`}>{added ? <Check size={12} /> : <Plus size={12} />}</Button>
  </div>
);

export const TermCrossCheck = ({ reqs, stdIds, setStdIds, selection, setSelection, focus, setFocus, defaultFunctions }) => {
  const { aiEnabled } = useAuth();
  const allFns = useMemo(() => Array.from(new Set(reqs.flatMap((r) => r.applicable_functions || []))).sort(), [reqs]);
  const [term, setTerm] = useState(focus?.term || "");
  const [fns, setFns] = useState(focus?.functions?.length ? focus.functions : defaultFunctions || []);
  const [allStds, setAllStds] = useState(false);
  const [res, setRes] = useState(null);
  const [ai, setAi] = useState(null);
  const [busy, setBusy] = useState("");

  const body = () => ({ term, functions: fns, standard_ids: stdIds, all_standards: allStds });
  const add = (rows) => {
    setStdIds((p) => Array.from(new Set([...p, ...rows.map((r) => r.standard_id)])));
    setSelection((p) => {
      const n = { ...p };
      rows.forEach((r) => { n[r.requirement_id] = n[r.requirement_id] || []; });
      return n;
    });
  };
  const search = async () => {
    if (!term.trim()) return toast.error("Enter a term, e.g. Operating Instruction or patch");
    if (!stdIds.length && !allStds) return toast.error("Pick standards first, or tick 'Also search all standards'");
    setBusy("search");
    try {
      const { data } = await api.post("/lens/term-search", body());
      setRes(data); setAi(null);
      setFocus((f) => ({ ...(f || {}), term: term.trim(), functions: fns }));
      if (!data.matches.length && !data.other_standards.length) toast.info("No requirements use that term");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const runAi = async () => {
    if (!term.trim()) return toast.error("Enter a term first");
    setBusy("ai");
    try {
      const data = await runAiJob("/lens/term-related-ai", body());
      setAi(data.suggestions || []);
      if (!data.suggestions?.length) toast.info("AI found no additional related wording");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const addOther = (o) => add(o.requirement_ids.map((id) => ({ requirement_id: id, standard_id: o.standard_id })));
  const selectOnly = () => {
    setSelection(Object.fromEntries(res.matches.map((m) => [m.requirement_id, selection[m.requirement_id] || []])));
    setStdIds(Array.from(new Set(res.matches.map((m) => m.standard_id))));
  };

  return (
    <div className="border border-[#071B3E]/25 bg-[#071B3E]/[0.02] p-2.5 space-y-2" data-testid="lens-term-crosscheck">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Cross-check a term across standards</div>
      <div className="flex gap-1.5">
        <Input data-testid="lens-term-input" value={term} onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())}
          placeholder="e.g. Operating Instruction, patch" className="rounded-none h-8 text-sm" />
        <Button data-testid="lens-term-search-btn" type="button" size="sm" onClick={search} disabled={!!busy} className="rounded-none h-8 shrink-0">
          <MagnifyingGlass size={14} className="mr-1" />{busy === "search" ? "Searching…" : "Search"}
        </Button>
      </div>
      <div className="text-[10px] text-muted-foreground">Functions (requirements must apply to at least one):</div>
      <FnChips all={allFns} value={fns} onChange={setFns} />
      <label className="flex items-center gap-2 text-xs cursor-pointer">
        <Checkbox data-testid="lens-term-all-standards" checked={allStds} onCheckedChange={(v) => setAllStds(!!v)} className="rounded-none" />
        Also search all standards (lists other standards that use the term)
      </label>
      {res && (
        <div className="border border-border bg-background" data-testid="lens-term-results">
          <div className="flex items-center gap-2 px-2 py-1">
            <span className="text-[11px] font-semibold flex-1">{res.matches.length} match{res.matches.length === 1 ? "" : "es"} in selected standards</span>
            {res.matches.length > 0 && <>
              <button type="button" data-testid="lens-term-add-all" onClick={() => add(res.matches)} className="text-[11px] underline">Add all</button>
              <button type="button" data-testid="lens-term-select-only" onClick={selectOnly} className="text-[11px] underline">Select only these</button>
            </>}
          </div>
          <div className="max-h-56 overflow-y-auto">
            {res.matches.map((m) => <MatchRow key={m.requirement_id} m={m} term={res.term} added={selection[m.requirement_id] !== undefined} onAdd={add} />)}
          </div>
          {res.other_standards.length > 0 && (
            <div className="border-t border-border px-2 py-1.5 space-y-1" data-testid="lens-term-other-standards">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Other standards using “{res.term}”</div>
              {res.other_standards.map((o) => (
                <div key={o.standard_id} className="flex items-center gap-2 text-xs">
                  <span className="font-mono flex-1">{o.standard_id} <span className="text-muted-foreground font-sans">· {o.count} requirement{o.count === 1 ? "" : "s"}</span></span>
                  <button type="button" data-testid={`lens-term-add-std-${o.standard_id}`} onClick={() => addOther(o)} className="text-[11px] underline">Add matches</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <Button data-testid="lens-term-ai-btn" type="button" variant="outline" onClick={runAi} disabled={!!busy || !aiEnabled}
        title={!aiEnabled ? "AI features are turned off" : "Uses 1 AI credit"} className="rounded-none w-full h-8 text-xs">
        <Sparkle size={14} weight="fill" className="mr-1" />{busy === "ai" ? "Finding related wording…" : "Find related wording (AI)"}
      </Button>
      {ai && ai.length > 0 && (
        <div className="border border-dashed border-primary/40" data-testid="lens-term-ai-results">
          {ai.map((m) => <MatchRow key={m.requirement_id} m={m} term="" reason={m.reason} added={selection[m.requirement_id] !== undefined} onAdd={add} />)}
        </div>
      )}
      <FocusInstructions value={focus?.instructions || ""} standardIds={stdIds} functions={fns} term={term}
        onChange={(v) => setFocus((f) => ({ ...(f || {}), instructions: v }))}
        onPicks={(picks) => add(picks)} />
      {focus?.term && (
        <div className="flex items-center gap-2 text-[11px]" data-testid="lens-focus-indicator">
          <span className="flex-1">Review focus: <b>{focus.term}</b>{focus.functions?.length ? ` · ${focus.functions.join(", ")}` : ""} — AI questions will probe this topic.</span>
          <button type="button" data-testid="lens-focus-clear" onClick={() => setFocus((f) => (f?.instructions ? { instructions: f.instructions } : null))} className="underline text-muted-foreground">Clear focus</button>
        </div>
      )}
    </div>
  );
};
